import {
  type BehaviorMapEventHandler,
  type MapGeolocationControlProps,
  ArrowLeftIcon,
  Button,
  IconButton,
  Lead,
  MapGeolocationCustomControl as GeolocationButton,
  MapListener,
  Paper,
  Slot,
  Text,
  Typography,
  useMapRef,
} from '@yandex-delivery/blocks'
import {useI18n} from '@yandex-delivery/core/i18n'
import {useDebouncedFunction} from '@yandex-delivery/hooks'
import {raise} from '@yandex-delivery/utils'
import {type ReactElement, useState} from 'react'

import {useFetchAddressPin} from '../../../../api/address'
import {useConfigContext} from '../../../../contexts'
import type {AddressPin} from '../../types'
import {PointMarkerIcon} from '../PointMarker/PointMarkerIcon'
import {toCenterLocation} from '../utils'
import * as classes from './MapModeSearch.classes'

type Props = {
  onAddressPinChange(addressPin: AddressPin): void
  onBackButtonClick: VoidFunction
}

export function MapModeSearch(props: Props): ReactElement {
  const {onAddressPinChange: actionDoneHandler, onBackButtonClick: backButtonClickHandler} = props
  const i18n = useI18n()

  const map = useMapRef() || raise('[MapModeSearch] "mapRef" not found')
  const [dragging, setDragging] = useState<boolean>(false)

  const {data: addressPin, isPending, mutate: fetchAddressPin} = useFetchAddressPin()

  const actionStartHandler: BehaviorMapEventHandler = () => {
    setDragging(true)
  }

  const actionEndHandler: BehaviorMapEventHandler = useDebouncedFunction(event => {
    fetchAddressPin(
      {coordinates: event.location.center},
      {
        onSettled: () => {
          setDragging(false)
        },
      },
    )
  }, 600)

  const geolocationChangeHandler: MapGeolocationControlProps['onGeolocatePosition'] = coordinates => {
    fetchAddressPin(
      {coordinates},
      {
        onSuccess: newAddressPin => {
          map.setLocation(toCenterLocation(newAddressPin.coordinates, 16))
        },
      },
    )
  }

  const clickHandler = () => {
    actionDoneHandler(addressPin || raise('[MapModeSearch][clickHandler] "addressPin" not found'))
  }

  const appConfig = useConfigContext()

  return (
    <>
      <div className={classes.pointMarker}>
        <PointMarkerIcon className={classes.pointMarkerIcon} dragging={dragging} type="source" draggable />
      </div>
      <MapListener onActionEnd={actionEndHandler} onActionStart={actionStartHandler} />
      <Paper className={classes.paper} flexDirection="column" float="bottom">
        <GeolocationButton
          classes={classes.geolocationButton}
          initialLocation={appConfig.location}
          onGeolocatePosition={geolocationChangeHandler}
          initializeLocation
        />
        <IconButton className={classes.backButton} onPress={backButtonClickHandler} shape="circle" variant="floating">
          <ArrowLeftIcon />
        </IconButton>
        <Slot size="l">
          <Text
            // @ts-expect-error Property 'Component' does not exist on type...
            Component={Typography}
            thickness="medium"
            variant="body1"
          >
            {i18n('bdui.pickup_points.search-by-address', 'Поиск по адресу')}
          </Text>
        </Slot>
        {addressPin && (
          <Slot size="l">
            <Lead>
              <div className={classes.titleIcon} />
            </Lead>
            <Text>
              <Typography variant="body2">{addressPin.title}</Typography>
              {addressPin.subtitle && (
                <Typography className={classes.subtitle} color="minor" variant="caption1">
                  {addressPin.subtitle}
                </Typography>
              )}
            </Text>
          </Slot>
        )}
        <div />
        <Slot pb="m" px="s" size="l">
          <Button
            disabled={dragging}
            loading={isPending}
            onPress={dragging ? undefined : clickHandler}
            variant="action"
            fullWidth
          >
            {i18n('bdui.pickup_points.button.continue', 'Продолжить')}
          </Button>
        </Slot>
      </Paper>
    </>
  )
}
